import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ApplicationRef,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { cookiesStorage, provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { buildRoutes } from './app.routes';
import { tokenAuthInterceptor } from './interceptor/token-auth.interceptor';
import { LanguageService } from './language.service';
import { TranslocoHttpLoader } from './transloco-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserAnimationsModule),
    { provide: LOCALE_ID, useValue: 'it-IT' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter([]),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includeHeaders: ['vendure-auth-token'],
      }),
    ),
    MessageService,
    provideHttpClient(withFetch(), withInterceptors([tokenAuthInterceptor])),
    provideTransloco({
      config: {
        availableLangs: ['it', 'en'],
        defaultLang: navigator.language.split('-')[0],
        reRenderOnLangChange: true,
        fallbackLang: 'it',
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoPersistLang({
      storage: { useValue: cookiesStorage() },
    }),

    provideApollo(() => {
      const hostname = environment.shopApiUrl;
      const httpLink = inject(HttpLink);
      const appRef = inject(ApplicationRef);
      const translocoService = inject(TranslocoService);
      const messageService: MessageService = inject(MessageService);

      const cache = new InMemoryCache({
        typePolicies: {
          Wishlist: {
            fields: {
              items: {
                merge(existing = [], incoming: any[], { readField }) {
                  const merged = new Map();
                  [...existing, ...incoming].forEach((item) => {
                    const id = readField('id', item);
                    if (id) merged.set(id, item);
                  });
                  return Array.from(merged.values());
                },
              },
            },
          },
        },
      });

      const requestLogger = new ApolloLink((operation, forward) => {
        return forward(operation).map((result) => {
          return result;
        });
      });

      const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        if (networkError) {
          const summary = translocoService.translate('message.serverError.summary');
          const detail = translocoService.translate('message.serverError.error.details');
          if ('status' in networkError && (networkError as any).status === 0) {
            if (messageService) {
              messageService.add({
                severity: 'error',
                summary,
                detail,
              });
              appRef.tick();
            }
          }
        }
      });

      const mainHttpLink = httpLink.create({
        uri: hostname,
        extractFiles: (body) => extractFiles(body, isExtractableFile),
      });

      return {
        link: ApolloLink.from([errorLink, requestLogger, mainHttpLink]),
        cache,
        devtools: {
          enabled: !environment.production,
        },
      };
    }),

    {
      provide: APP_INITIALIZER,
      useFactory:
        (langService: LanguageService, translocoService: TranslocoService, router: Router) =>
        async () => {
          const lang = langService.getInitialLang();

          translocoService.setActiveLang(lang);
          await firstValueFrom(translocoService.load(lang));

          document.documentElement.lang = translocoService.getActiveLang();

          const routes = await buildRoutes(translocoService);
          router.resetConfig(routes);
        },
      deps: [LanguageService, TranslocoService, Router],
      multi: true,
    },
  ],
};
